// ***************************************
// Definition of game token SceneController
// ***************************************
import { flowController, gameController, inventoryController, inviteFriendManager } from "../../game_theme/flow_fb/flowController";
import { tbPhysics2, Box2D } from "../engine/physics/tbPhysics2";
import { tbContainer } from "../engine/tbContainer";
import { tbGame } from "../engine/tbGame";
import { tbGraphics } from "../engine/tbGraphics";
import { tbVector } from "../engine/tbVector";
import { tgfHooks } from "../framework/hooks/hooks";
import { tgfAudio } from "../framework/tgfAudio";
import { tgfDictionary } from "../framework/tgfDictionary";
import { tgfGraphics } from "../framework/tgfGraphics";
import { Explosion } from "../settings/Explosion";
import { PhysicsSettings, JointType } from "../settings/PhysicsSettings";
import { LevelSettings, CameraSettings, PigSettings, GraphicsSettings, ScoreSettings, PowerUpSettings, NameSettings, MiscSettings, KillerPlanes, AbilitySettings, CardSettings } from "../settings/Settings";
import { SoundManager } from "../settings/SoundManager";
import { PhysicsObjectDefinition } from "../settings/blocks";
import { AngryAnalytics } from "./Analytics/Analytics";
import { s_indicator_noplace, s_swapPig_selected, s_buttonpowerbg, s_button_beam, s_blueside, s_redside } from "./AssetDefinitions/ImageDefinitions";
import { s_smokePuff, s_scope_sparkle, timer_animation } from "./AssetDefinitions/SpineDefinitions";
import { BalloonObjectDefault } from "./BalloonObject";
import { BirdObjectBig } from "./BirdObjects/BirdObjectBig";
import { BirdObjectBlack } from "./BirdObjects/BirdObjectBlack";
import { BirdObjectBlue } from "./BirdObjects/BirdObjectBlue";
import { BirdObjectMatilda } from "./BirdObjects/BirdObjectMatilda";
import { BirdObjectRed } from "./BirdObjects/BirdObjectRed";
import { BirdObjectYellow } from "./BirdObjects/BirdObjectYellow";
import { BirdTrail } from "./BirdTrail";
import { compressData } from "./Compression";
import { DailyRewardManager } from "./DailyRewardManager";
import { EventSystem } from "./Events/EventSystem";
import { Events } from "./Events/Events";
import { ExplosionToken } from "./ExplosionToken";
import { floater } from "./Floater";
import { MomentPlayer } from "./MomentPlayer";
import { PhysicsObject, getDamageMultiplier, getVelocityMultiplier } from "./PhysicsObject";
import { PigObject } from "./PigObject";
import { PracticeFloaterDialog } from "./PracticeFloaterDialog";
import { ReplayData } from "./ReplayData";
import { ReplayLogger } from "./ReplayLogger";
import { Slingshot } from "./Slingshot";
import { SpineEffect } from "./SpineEffect";
import { playController } from "./gameController";
var idt = 0;
export var SceneControllerState = {
    NONE: idt++,
    INITIALIZING: idt++,
    TURN_STARTED: idt++,
    REPLAY_AVAILABLE: idt++,
    REPLAYING: idt++,
    POWERUP_AVAILABLE: idt++,
    POWERUP_APPLYING: idt++,
    BIRD_AVAILABLE: idt++,
    GAME_WORKING: idt++,
    WAITING_FOR_OPPONENT: idt++,
    GAME_OVER: idt++,
    POWERUP_QUESTION: idt++ // ui hidden, show powerup bar, ask player if they want to use a powerup
};
export var physics = null;
// The constructor for the SceneController game token. 
export var SceneController = function (x, y, depth, levelIndex) {
    "use strict";
    // Properties used by the engine
    this.depth = depth;
    this.layer = 0;
    this.visible = true;
    this.active = true;
    // Your own properties
    this.x = x;
    this.y = y;
    this.cameraShake = 0;
    this.randomTracker = 0;
    this.checkPhysicsStop = this.idleBlockTimeout;
    this.killers = { left: 0, top: 0, bottom: 0, right: 0, pigsBottom: 0 };
    this.slingshotPerimeter = { left: 0, right: 0 };
    this.levelData = LevelSettings.levelList[levelIndex].level;
    this.jointList = [];
    this.idleBlockTimeout = 60;
    this.fingerOne = { x: 0, y: 0, touch: false };
    this.futureState = SceneControllerState.NONE;
    this.state = SceneControllerState.NONE;
    this.allBlocksAreIdle = false;
    this.idleLastStep = false;
    this.idleSteps = 0;
    this.stoppedIdling = false;
    this.entryData = null;
    this.canUsePowerUp = true; // so you can't multiclick
    this.uiWaitTime = 0;
    this.container = new tbContainer(tgfGraphics.areaWidth, tgfGraphics.areaHeight);
    this.levelOffsetContainer = new tbContainer();
    this.container.addChild(this.levelOffsetContainer);
    this.setupSlingshots();
    this.birdTrailContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.birdTrailContainer);
    this.levelContainer = new tbContainer();
    this.pigContainer = new tbContainer();
    this.smokePuffContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.smokePuffContainer);
    this.explosionContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.explosionContainer);
    this.impactContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.impactContainer);
    this.floaterContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.floaterContainer);
    this.particleContainer = new tbContainer();
    this.levelOffsetContainer.addChild(this.particleContainer);
    this.placedPowerUpIcons = [];
    this.invalidPosImage = s_indicator_noplace.clone();
    this.invalidPosImage.setAlpha(0);
    this.invalidPosImage.setScale(PhysicsSettings.physicsScale, PhysicsSettings.physicsScale);
    this.floaterContainer.addChild(this.invalidPosImage);
    this.replayData = new ReplayData();
    this.playback = null;
    this.replayStateToLoad = null;
    this.launchReady = false;
    this.pigSelectedForSwap = null;
    this.firstBirdFired = false;
    // Add it to the game engine
    tbGame.addToken(this);
    tbGame.addTokenTags(this, "play");
};
export var Layer = {
    block: 0,
    smokePuff: 1,
    explosion: 2,
    impact: 3,
    floater: 4,
    particle: 5,
};
export function getContainerForLayer(layer) {
    return playController.sceneController.internalGetContainerForLayer(layer);
}
export function blockFactory(name, uid, x, y, angle, container) {
    if (name == "RedBird")
        return new BirdObjectRed(name, uid, x, y, angle, container);
    if (name == "BlueBird")
        return new BirdObjectBlue(name, uid, x, y, angle, container);
    if (name == "YellowBird")
        return new BirdObjectYellow(name, uid, x, y, angle, container);
    if (name == "BlackBird")
        return new BirdObjectBlack(name, uid, x, y, angle, container);
    if (name == "BigBird")
        return new BirdObjectBig(name, uid, x, y, angle, container);
    if (name == "Matilda")
        return new BirdObjectMatilda(name, uid, x, y, angle, container);
    if (name["startsWith"]("PIG_"))
        return new PigObject(name, uid, x, y, angle, container);
    if (name["startsWith"]("MISC_BALLOON_"))
        return new BalloonObjectDefault(name, uid, x, y, angle, container);
    if (name["startsWith"]("MISC_EXPLOSIVE_")) {
        var temp = new PhysicsObject(name, uid, x, y, angle, container);
        temp.isTNT = true;
        return temp;
    }
    return new PhysicsObject(name, uid, x, y, angle, container);
}
export function globalAddExplosion(x, y, explosionType) {
    playController.sceneController.addExplosion(x, y, explosionType);
}
export function globalAddConeBlast(x, y, angle) {
    playController.sceneController.addConeBlast(x, y, angle);
}
SceneController.prototype.getStateName = function (sceneControllerState) {
    "use strict";
    for (var prop in SceneControllerState) {
        if (SceneControllerState.hasOwnProperty(prop)) {
            if (SceneControllerState[prop] === sceneControllerState)
                return prop.toString();
        }
    }
    return "UNDEFINED (" + sceneControllerState + ")";
};
SceneController.prototype.internalGetContainerForLayer = function (layer) {
    switch (layer) {
        case Layer.block:
            return this.levelContainer;
        case Layer.smokePuff:
            return this.smokePuffContainer;
        case Layer.explosion:
            return this.explosionContainer;
        case Layer.impact:
            return this.impactContainer;
        case Layer.floater:
            return this.floaterContainer;
        case Layer.particle:
            return this.particleContainer;
    }
};
// handleAdd is called when the token is (re-) added to the game. 
SceneController.prototype.handleAdd = function () {
    "use strict";
    this.resetPhysics();
    ReplayLogger.startRecording();
    this.powerupClicked = this.removeSelectedPigEffect.bind(this);
    EventSystem.subscribe(Events.POWERUP_CLICKED, this.powerupClicked);
    this.powerupCanceled = this.onPowerupCanceled.bind(this);
    EventSystem.subscribe(Events.POWERUP_CANCELED, this.powerupCanceled);
};
SceneController.prototype.resetPhysics = function () {
    "use strict";
    var self = this;
    this.balls = [];
    this.levelBlocks = [];
    this.pigBlocks = [];
    this.explosions = [];
    this.coneBlasts = [];
    if (physics) {
        tbGame.removeToken(physics);
    }
    physics = new tbPhysics2(PhysicsSettings.physicsScale, PhysicsSettings.physicsTimeStep, PhysicsSettings.physicsMaxSteps, PhysicsSettings.gravity, 0, function () { self.handlePhysicsStep(); });
    this.contactListener = new Box2D.JSContactListener();
    this.contactListener["PreSolve"] = function (pContact) {
        self.PreSolveHandler(pContact);
    };
    this.contactListener["PostSolve"] = function (pContact) {
        self.PostSolveHandler(pContact);
    };
    this.contactListener["EndContact"] = function () { };
    this.contactListener["BeginContact"] = function (pContact) {
        self.contactHandler(pContact);
    };
    physics.world.SetContactListener(this.contactListener);
};
// handleRemove is called when the token is removed from the game.
SceneController.prototype.handleRemove = function () {
    "use strict";
    tgfAudio.stop("music");
    tgfAudio.stop("ambience");
    tbGame.removeToken(physics);
    physics = undefined;
    tbGame.removeToken(this.slingshotLeft);
    this.slingshotLeft = undefined;
    tbGame.removeToken(this.slingshotRight);
    this.slingshotRight = undefined;
    this.balls = [];
    this.levelBlocks = [];
    EventSystem.unsubscribe(Events.POWERUP_CLICKED, this.powerupClicked);
    EventSystem.unsubscribe(Events.POWERUP_CANCELED, this.powerupCanceled);
    this._destroyAllBirdTrails();
};
// handleBeginStep is called each step before processing other events.
SceneController.prototype.handleBeginStep = function (timePassed) {
    "use strict";
    if (!this._timer) {
        return;
    }
    this._timer -= timePassed;
    if (this._timer <= 0 && !this.isReleased) {
        SoundManager.playEffect(SoundManager.BIRD_STATE.IDLE, this.sounds);
        this._timer = this.idleTimer * 2;
    }
};
// handleTouchDown is called when a touch is started or if a mouse button is pressed.
SceneController.prototype.handleTouchDown = function (finger, x, y) {
    "use strict";
    if (finger == 0) {
        this.fingerOne.touch = true;
        this.fingerOne.startX = x;
        this.fingerOne.startY = y;
    }
    if (this.balls.length > 0 && this.state != SceneControllerState.REPLAYING) {
        this.balls[this.balls.length - 1].needToActivateSpecialPower = true;
    }
};
SceneController.prototype._updateAimCallbacks = function () {
    "use strict";
    if (this.isAimingStart && !this._aimStarted) {
        this._aimStarted = true;
        this.isAimingStart();
    }
    if (this.isAiming)
        this.isAiming();
};
SceneController.prototype.enableMightyScope = function () {
    "use strict";
    if (playController.hasMightyScope) {
        return;
    }
    playController.hasMightyScope = true;
    var slingshot = this.getSlingshot(false);
    new SpineEffect(slingshot.x + 1, slingshot.y - 5, s_smokePuff, "s_smoke_puff_chuck", getContainerForLayer(Layer.smokePuff), 1.5, "mighty_scope_appear", function () {
        slingshot.createDots();
        slingshot.toggleMightyScope(true);
    }, this, function () {
        new SpineEffect(slingshot.x + 1, slingshot.y - 5, s_scope_sparkle, "sparkle_effect", getContainerForLayer(Layer.particle), 2);
    });
};
SceneController.prototype.disableMightyScope = function () {
    "use strict";
    if (!playController.hasMightyScope) {
        return;
    }
    playController.hasMightyScope = false;
    var slingshot = this.getSlingshot(false);
    new SpineEffect(slingshot.x + 1, slingshot.y - 5, s_smokePuff, "s_smoke_puff_chuck", getContainerForLayer(Layer.smokePuff), 1.5, "mighty_scope_appear", function () {
        slingshot.createDots();
        slingshot.toggleMightyScope(false);
    });
};
// handleTouchUp is called when a touch is ended or if a mouse button is released.
SceneController.prototype.handleTouchUp = function (finger, x, y) {
    "use strict";
    if (finger == 0)
        this.fingerOne.touch = false;
};
SceneController.prototype.handleEndStep = function (timePassed) {
    "use strict";
    if (this.placedPowerUpIcons) {
        for (var i = 0; i < this.placedPowerUpIcons.length; i++) {
            var element = this.placedPowerUpIcons[i];
            if (element.step) {
                this.placedPowerUpIcons[i].step(timePassed);
            }
        }
    }
    if (this.invalidPosStart) {
        var time = Date.now() - this.invalidPosStart;
        var seconds = time / 1000;
        var alpha = 2 - seconds;
        this.invalidPosImage.setAlpha(alpha);
        if (alpha <= 0) {
            this.invalidPosStart = 0;
        }
    }
    for (var i = 0; i < this.levelBlocks.length; i++) {
        var b = this.levelBlocks[i];
        b.step(timePassed);
        if (b.isPig && b.y > this.killers.pigsBottom) {
            b.applyDamage(Math.ceil(b.maxHealth + b.getShield()));
        }
    }
    for (i = 0; i < this.balls.length; i++) {
        var ball = this.balls[i];
        ball.step(timePassed);
        if (ball.firstContactFrame) {
            this.cameraShake = CameraSettings["cameraShake"]["birdImpactShake"];
            ball.firstContactFrame = false;
            playController.handleFirstContact();
        }
    }
    var impact = this.getSlingshot().trailImpact;
    for (var a = 0; a < this.pigBlocks.length; a++) {
        var pig = this.pigBlocks[a];
        var needsToBeScared = false;
        if (!pig.body)
            continue;
        if (playController.isShooting) {
            if (Math.pow(pig.x - impact.x, 2) + Math.pow(pig.y - impact.y, 2) < PigSettings["scaredRadius"] * PigSettings["scaredRadius"]) {
                needsToBeScared = true;
            }
        }
        if (needsToBeScared) {
            if (pig.currentState != PigObject.STATE.SCARED && pig.currentState != PigObject.STATE.SCARED_JUMP) {
                pig.animationState(PigObject.STATE.SCARED_JUMP);
            }
        }
        else {
            if (pig.currentState != PigObject.STATE.IDLE) {
                pig.animationState(PigObject.STATE.IDLE);
            }
        }
        //pig.isMoving();
    }
    if (this.state == SceneControllerState.GAME_WORKING) {
        if (playController.uiController.uiCards.getFirstCard()) {
            var birdsHadContact = false;
            for (var a = 0; a < this.balls.length; a++) {
                if (this.balls[a] && this.balls[a].firstContact) {
                    birdsHadContact = true;
                    break;
                }
            }
            if (birdsHadContact || this.balls.length <= 0) {
                if (this.uiWaitTime >= GraphicsSettings.uiWaitTimeAfterImpact) {
                    this.changeState(SceneControllerState.BIRD_AVAILABLE);
                }
                this.uiWaitTime += timePassed;
            }
        }
        else if (this.allBlocksAreIdle) {
            if (this.balls.length > 0) {
                this.destroyAllBirds();
                this.checkPhysicsStop = this.idleBlockTimeout;
            }
            this.changeState(SceneControllerState.WAITING_FOR_OPPONENT);
        }
    }
    if (this.state == SceneControllerState.GAME_WORKING || this.state == SceneControllerState.BIRD_AVAILABLE) {
        if (this.allBlocksAreIdle && this.checkForGameOver()) {
            this.changeState(SceneControllerState.GAME_OVER);
        }
    }
    if (this.state === SceneControllerState.BIRD_AVAILABLE || this.state === SceneControllerState.POWERUP_QUESTION) {
        if (playController.uiController.uiPowerUps.getCurrentPowerUp() !== null) {
            this.changeState(SceneControllerState.POWERUP_AVAILABLE);
        }
        else {
            this.pigSelectedForSwap = null;
        }
    }
    if (Date.now() > gameController.mightyScopeEnd && playController.gameType !== "practice") {
        this.disableMightyScope();
    }
    SoundManager.update(timePassed);
};
SceneController.prototype.checkForGameOver = function () {
    "use strict";
    this.entryData["initiatingPlayerData"]["totalPigs"] = this.countPigs(true);
    this.entryData["otherPlayerData"]["totalPigs"] = this.countPigs(false);
    this.entryData["initiatingPlayerData"]["won"] = this.entryData["otherPlayerData"]["totalPigs"] == 0;
    this.entryData["otherPlayerData"]["won"] = this.entryData["initiatingPlayerData"]["totalPigs"] == 0;
    if (this.entryData["initiatingPlayerData"]["won"] && this.entryData["otherPlayerData"]["won"]) {
        if (this.entryData["initiatingPlayerData"]["score"] > this.entryData["otherPlayerData"]["score"]) {
            this.entryData["otherPlayerData"]["won"] = false;
        }
        else {
            this.entryData["initiatingPlayerData"]["won"] = false;
        }
    }
    if (this.entryData["initiatingPlayerData"]["won"] || this.entryData["otherPlayerData"]["won"]) {
        return true;
    }
    return false;
};
SceneController.prototype.addScoreAndSpawnFloaters = function (points, block) {
    "use strict";
    if (this.state === SceneControllerState.REPLAYING) {
        return;
    }
    if (this.firstBirdFired) {
        if (!playController.isInitiatingPlayer) {
            this.entryData["otherPlayerData"]["score"] += points;
        }
        else {
            this.entryData["initiatingPlayerData"]["score"] += points;
        }
        // We don't need to update the UI if we don't display the score
        //playController.updateUI();
    }
    if (block.isDead() && block.isPig) {
        var msg;
        var fontOverride = { fontSize: ScoreSettings.pigKillFontSize + ScoreSettings.killstreakGrowth };
        if (block.isAlly) {
            var index = this._getRandomIndexInArray(ScoreSettings.allyPigKillMessages, this.lastAllyMessageIndex);
            this.lastAllyMessageIndex = index;
            msg = tgfDictionary.getText(ScoreSettings.allyPigKillMessages[index]);
            fontOverride.fillColor = ScoreSettings.allyPigKillColor;
            fontOverride.strokeColor = ScoreSettings.allyPigKillStrokeColor;
        }
        else {
            this.currentKillStreak++;
            fontOverride.fillColor = ScoreSettings.enemyPigKillColor;
            if (ScoreSettings.killstreakMessages[this.currentKillStreak]) {
                msg = tgfDictionary.getText(ScoreSettings.killstreakMessages[this.currentKillStreak]);
                fontOverride.fontSize = ScoreSettings.pigKillFontSize + this.currentKillStreak * ScoreSettings.killstreakGrowth;
            }
            else {
                var index = this._getRandomIndexInArray(ScoreSettings.enemyPigKillMessages, this.lastEnemyMessageIndex);
                this.lastEnemyMessageIndex = index;
                msg = tgfDictionary.getText(ScoreSettings.enemyPigKillMessages[index]);
            }
        }
        floater.addTextFloater(msg, block.x, block.y, fontOverride);
    }
    // if (points >= ScoreSettings.floaterThreshold && !block.isPig) {
    // 	floater.addFloater(points, block.x, block.y);
    // }
};
SceneController.prototype._getRandomIndexInArray = function (array, lastIndex) {
    var index = Math.floor(Math.random() * array.length);
    while (index === lastIndex) {
        index = Math.floor(Math.random() * array.length);
    }
    return index;
};
SceneController.prototype.getPowerUpPlacementDelay = function (power) {
    for (var index = 0; index < PowerUpSettings.options.length; index++) {
        var element = PowerUpSettings.options[index];
        if (element.kind === power) {
            if (element.placementDelay) {
                return element.placementDelay;
            }
            else {
                return PowerUpSettings.timeBetweenActionAndResume;
            }
        }
    }
    return PowerUpSettings.timeBetweenActionAndResume;
};
SceneController.prototype.managePowerUp = function () {
    "use strict";
    var power = playController.uiController.uiPowerUps.getCurrentPowerUp();
    this.managePowerUpVisuals();
    if (power !== null && this.fingerOne.touch && !this.previousTouch && this.fingerOne.y < tgfGraphics.areaHeight - 140 && !flowController.isOffline) {
        this.previousTouch = true;
        var mouse = new tbVector(this.fingerOne.x, this.fingerOne.y);
        var localPos = tbGraphics.toLocal(this.levelContainer, new tbVector(this.fingerOne.x, this.fingerOne.y));
        if (power == NameSettings.shield) {
            if (this.canUsePowerUp === false)
                return;
            var clickedPig = false;
            for (var a = 0; a < this.levelBlocks.length; a++) {
                var b = this.levelBlocks[a];
                if (!b.isPig)
                    continue;
                var pig = this.levelBlocks[a];
                if (!this.fingerOne.touch)
                    continue;
                var pigPosition = tbGraphics.toGlobal(pig.container, new tbVector(pig.x, pig.y));
                if (mouse.distance(pigPosition) < 30) {
                    if (!pig.isAlly) {
                        floater.addTextFloater(tgfDictionary.getText("PU_SELECT_BLUE_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                    else if (pig.shield > 0) {
                        floater.addTextFloater(tgfDictionary.getText("PU_HAS_SHIELD_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                    else {
                        this.createPowerUpIcon(localPos, power);
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].pigIndex = a;
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].selectedPig = pig.id;
                        this.canUsePowerUp = false;
                        var _this = this;
                        var momentPlayer = new MomentPlayer(_this.placePowerup, this);
                        momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction, this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1]);
                        momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction + this.getPowerUpPlacementDelay(power), { isCleanup: true, power: this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].power });
                        playController.uiController.uiPowerUps.onPowerUpPlaced();
                        this.stoppedIdling = true;
                        this.replayData.setPowerupIcon({ position: { x: localPos.x, y: localPos.y }, power: power });
                        this.revealPigs();
                    }
                    clickedPig = true;
                    break;
                }
            }
            if (!clickedPig) {
                floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                this.showInvalidPosition(localPos.x, localPos.y);
                return;
            }
        }
        else if (power == NameSettings.swapPigs) {
            if (this.canUsePowerUp === false)
                return;
            var clickedPig = false;
            for (var a = 0; a < this.levelBlocks.length; a++) {
                var b = this.levelBlocks[a];
                if (!b.isPig)
                    continue;
                var pig = this.levelBlocks[a];
                if (!this.fingerOne.touch)
                    continue;
                var pigPosition = tbGraphics.toGlobal(pig.container, new tbVector(pig.x, pig.y));
                if (mouse.distance(pigPosition) < 30) {
                    clickedPig = true;
                    if (this.pigSelectedForSwap === null) {
                        this.pigSelectedForSwap = this.levelBlocks[a];
                        this.pigSelectedForSwap._selectedPigEffect = s_swapPig_selected.clone();
                        this.pigContainer.addChild(this.pigSelectedForSwap._selectedPigEffect);
                        this.pigSelectedForSwap._selectedPigEffect.setPosition(pigPosition.x, pigPosition.y);
                        this.pigSelectedForSwap._selectedPigEffect.setScale(PhysicsSettings.physicsScale, PhysicsSettings.physicsScale);
                    }
                    else if (this.pigSelectedForSwap && pig.getSize() !== this.pigSelectedForSwap.getSize()) {
                        floater.addTextFloater(tgfDictionary.getText("PU_SELECT_PIG_SIZE_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                    else if (pig == this.pigSelectedForSwap) {
                        floater.addTextFloater(tgfDictionary.getText("PU_SELECT_PIG_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                    else {
                        var placeToPlaceIt = { x: pig.x, y: pig.y };
                        this.createPowerUpIcon({ x: this.pigSelectedForSwap.x, y: this.pigSelectedForSwap.y }, power);
                        this.createPowerUpIcon(placeToPlaceIt, power);
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].pigIndex1 = pig.id;
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].pigIndex2 = this.pigSelectedForSwap.id;
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].selectedPig1 = pig;
                        this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].selectedPig2 = this.pigSelectedForSwap;
                        this.canUsePowerUp = false;
                        var _this = this;
                        var momentPlayer = new MomentPlayer(_this.placePowerup, this);
                        momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction, this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1]);
                        momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction + this.getPowerUpPlacementDelay(power), { isCleanup: true, power: this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].power });
                        playController.uiController.uiPowerUps.onPowerUpPlaced();
                        this.stoppedIdling = true;
                        this.replayData.setPowerupIcon({ position: { x: placeToPlaceIt.x, y: placeToPlaceIt.y }, power: power });
                        this.replayData.setPowerupIcon({ position: { x: this.pigSelectedForSwap.x, y: this.pigSelectedForSwap.y }, power: power });
                        this.revealPigs();
                        this.removeSelectedPigEffect();
                        break;
                    }
                }
            }
            if (!clickedPig) {
                floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                this.showInvalidPosition(localPos.x, localPos.y);
                return;
            }
        }
        else {
            if (this.fingerOne.touch) {
                if (this.canUsePowerUp === false)
                    return;
                if (power == NameSettings.bombDrop) {
                    var bottom = new tbVector(localPos.x, localPos.y + PowerUpSettings.tntSettings.maximumFallDistance);
                    var rayResult = physics.rayCast(localPos, bottom);
                    if (!rayResult.hit) {
                        floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                    else {
                        for (var i = 0; i < this.levelBlocks.length; i++) {
                            if (this.levelBlocks[i].definition.materialName !== "Static") {
                                // Don't even check non static ground for performance
                                continue;
                            }
                            var fixture = this.levelBlocks[i].body.GetFixtureList();
                            var testCoords = new Box2D.b2Vec2(localPos.x * PhysicsSettings.physicsScale, localPos.y * PhysicsSettings.physicsScale);
                            var testPoint = 0;
                            // Loop through all of the fixtures of a body and test each one
                            while (fixture.e) {
                                testPoint = fixture.TestPoint(testCoords);
                                if (testPoint !== 0) {
                                    floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                                    this.showInvalidPosition(localPos.x, localPos.y);
                                    return;
                                }
                                fixture = fixture.GetNext();
                            }
                        }
                    }
                }
                else if (power == NameSettings.stoneNator) {
                    if (this.findBlocksInRadius(localPos, PowerUpSettings.stoneNatorSettings.radius, ["Ice", "Wood"]).length == 0) {
                        floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                }
                else if (power == NameSettings.freezeRay) {
                    if (this.findBlocksInRadius(localPos, PowerUpSettings.freezeRaySettings.radius, ["Stone", "Wood"]).length == 0) {
                        floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                        this.showInvalidPosition(localPos.x, localPos.y);
                        return;
                    }
                }
                if (localPos.x < this.slingshotLeft.x + 5 || localPos.x > this.slingshotRight.x - 5) {
                    floater.addTextFloater(tgfDictionary.getText("PU_PLACEMENT_INVALID_TEXT"), localPos.x, localPos.y - ScoreSettings.textFloaterOffset);
                    this.showInvalidPosition(localPos.x, localPos.y);
                    return;
                }
                // Push the icon into the array
                this.createPowerUpIcon(localPos, power);
                // Tell the game that playtime is over
                this.canUsePowerUp = false;
                var _this = this;
                // Set up the moments; place icon in X time, cleanup in Y time.
                var momentPlayer = new MomentPlayer(_this.placePowerup, this);
                momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction, this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1]);
                momentPlayer.addEvent(PowerUpSettings.timeBetweenPlacementAndAction + this.getPowerUpPlacementDelay(power), { isCleanup: true, power: this.placedPowerUpIcons[this.placedPowerUpIcons.length - 1].power });
                // Hide the powerup UI
                playController.uiController.uiPowerUps.onPowerUpPlaced();
                this.stoppedIdling = true;
                // Setup the replay event
                this.replayData.setPowerupIcon({ position: { x: localPos.x, y: localPos.y }, power: power });
            }
        }
    }
    else if (!this.fingerOne.touch) {
        this.previousTouch = false;
    }
};
SceneController.prototype.managePowerUpVisuals = function () {
    if (!this.canUsePowerUp) {
        return;
    }
    if (this.pigSelectedForSwap && !this.pigSelectedForSwap.body) {
        if (playController && playController.uiController.uiPowerUps) {
            playController.uiController.uiPowerUps.cancelSelection();
        }
        return;
    }
    var power = playController.uiController.uiPowerUps.getCurrentPowerUp();
    if (power == NameSettings.shield) {
        for (var i = 0; i < this.levelBlocks.length; i++) {
            var b = this.levelBlocks[i];
            if (b.isPig && b.isAlly && b.shield <= 0 && b.image) {
                b.image.setAlpha(0.5 + 0.2 * Math.sin(physics.stepCount * 0.2));
            }
        }
    }
    else if (power == NameSettings.swapPigs) {
        for (var i = 0; i < this.levelBlocks.length; i++) {
            var b = this.levelBlocks[i];
            if (b.isPig && b.image) {
                if (!this.pigSelectedForSwap || (b.getSize() === this.pigSelectedForSwap.getSize())) {
                    b.image.setAlpha(0.5 + 0.2 * Math.sin(physics.stepCount * 0.2));
                }
                else {
                    b.image.setAlpha(1);
                }
                if (this.pigSelectedForSwap && this.pigSelectedForSwap.id === b.id) {
                    b.image.setAlpha(1);
                }
            }
        }
    }
};
SceneController.prototype.showInvalidPosition = function (x, y) {
    this.invalidPosImage.setPosition(x, y);
    this.invalidPosImage.setAlpha(1);
    this.invalidPosImagePos = new tbVector(x, y);
    this.invalidPosStart = Date.now();
};
SceneController.prototype.placePowerup = function (iconObject) {
    "use strict";
    var power = iconObject.power;
    var position = { x: iconObject.x, y: iconObject.y };
    if (!physics || flowController.isOffline) {
        return;
    }
    else if (iconObject.isCleanup === true) {
        playController.uiController.uiPowerUps.onPowerUpSpawned(power);
        playController.sceneBuilder.cameraToSlingshot(false);
        this.cleanUpPowerUp(true);
        return;
    }
    this.removePlacedPowerupIcons();
    this.usedPowerup = true;
    this.usedPowerupThisTurn = true;
    if (power === NameSettings.stoneNator) {
        this.replayData.setStone({ position: position });
        this.createStone(position);
    }
    else if (power == NameSettings.bombDrop) {
        this.replayData.setTNT({ position: position });
        this.createTNT(position);
    }
    else if (power == NameSettings.freezeRay) {
        this.replayData.setIce({ position: position });
        this.createIce(position);
    }
    else if (power == NameSettings.shield) {
        var p = this.levelBlocks.find(function (e) {
            return e.id === iconObject.selectedPig;
        });
        if (p) {
            this.replayData.setLife({ index: iconObject.pigIndex });
            p.activateShield(PowerUpSettings.shieldSettings.health);
        }
    }
    else if (power == NameSettings.swapPigs) {
        var pig1 = this.levelBlocks.find(function (e) {
            return e.id === iconObject.pigIndex1;
        });
        var pig2 = this.levelBlocks.find(function (e) {
            return e.id === iconObject.pigIndex2;
        });
        if (pig1 && pig2) {
            this.swapPigs(iconObject.pigIndex1, iconObject.pigIndex2);
            this.replayData.setSwapPigs({ "index1": iconObject.pigIndex1, "index2": iconObject.pigIndex2 });
        }
    }
    SoundManager.playEffect("spawn", SoundManager.PowerUpCollection[power]);
    this.firstLevelSave = false;
};
SceneController.prototype.revealPigs = function () {
    "use strict";
    for (var b = 0; b < this.levelBlocks.length; b++) {
        if (this.levelBlocks[b].isPig && !this.levelBlocks[b].invisible && this.levelBlocks[b].image) {
            this.levelBlocks[b].image.setAlpha(1);
        }
    }
};
SceneController.prototype.removeSelectedPigEffect = function () {
    "use strict";
    if (this.pigSelectedForSwap && this.pigSelectedForSwap._selectedPigEffect) {
        this.pigContainer.removeChild(this.pigSelectedForSwap._selectedPigEffect);
        delete this.pigSelectedForSwap._selectedPigEffect;
    }
};
SceneController.prototype.cleanUpPowerUp = function (consume) {
    "use strict";
    this.revealPigs();
    var currentPowerUpIndex = playController.uiController.uiPowerUps.getCurrentPowerUpIndex();
    for (var i = 0; i < this.placedPowerUpIcons.length; i++) {
        this.placedPowerUpIcons[i] = null;
    }
    this.placedPowerUpIcons = [];
    if ( /*power === null && */currentPowerUpIndex > -1 && consume == true) {
        var _powerUpSlots = inventoryController.getPowerUpSlots(), _powerUpUsed = _powerUpSlots[currentPowerUpIndex].powerUp, powerType = PowerUpSettings.options[_powerUpUsed].kind;
        AngryAnalytics.reportPowerupType(powerType);
        AngryAnalytics.reportPowerupUsed();
        // consuming the power up
        inventoryController.consumePowerUp(currentPowerUpIndex);
        this.changeState(SceneControllerState.POWERUP_APPLYING);
    }
    else {
        this.changeState(SceneControllerState.BIRD_AVAILABLE);
    }
    if (playController.uiController.uiPowerUps) {
        playController.uiController.uiPowerUps.cancelSelection();
    }
};
SceneController.prototype.findInSceneGraphByID = function (id) {
    "use strict";
    return this.levelBlocks.filter(function (obj) {
        return obj.id == id;
    });
};
SceneController.prototype.managePowerUpReplay = function (power, properties) {
    "use strict";
    switch (power) {
        case "shield":
            if (this.levelBlocks[properties.index].isPig) {
                var pig = this.levelBlocks[properties.index];
                pig.activateShield(PowerUpSettings.shieldSettings.health);
            }
            else {
                console.log("False Pig Index");
            }
            SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["Shield"]);
            break;
        case "pigSwap":
            this.swapPigs(properties["index1"], properties["index2"]);
            SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["SwapPigs"]);
            break;
        case "TNT":
            this.createTNT(properties.position);
            SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["BombDrop"]);
            break;
        case "generic":
            this.createPowerUpIcon(properties.position, properties.power);
            // TODO: Play a generic sound effect on placing a powerup (pending design)
            //SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["Shield"]);
            break;
        case "ice":
            this.createIce(properties.position);
            SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["FreezeRay"]);
            break;
        case "stone":
            this.createStone(properties.position);
            SoundManager.playEffect("spawn", SoundManager.PowerUpCollection["StoneNator"]);
            break;
    }
};
SceneController.prototype.removePlacedPowerupIcons = function () {
    for (var index = 0; index < this.placedPowerUpIcons.length; index++) {
        this.placedPowerUpIcons[index].layerContainer.removeChild(this.placedPowerUpIcons[index]);
        if (this.placedPowerUpIcons[index].effect) {
            tbGame.removeToken(this.placedPowerUpIcons[index].effect);
        }
    }
    this.placedPowerUpIcons = [];
};
SceneController.prototype.createPowerUpIcon = function (pos, power) {
    "use strict";
    var self = this;
    var pwrupBgImg;
    var bg;
    var layer = getContainerForLayer(Layer.smokePuff);
    for (var index = 0; index < PowerUpSettings.options.length; index++) {
        var element = PowerUpSettings.options[index];
        if (element.kind === power) {
            bg = s_buttonpowerbg.clone();
            var pwrupImg = element.image.clone();
            pwrupBgImg = element.bgImg.clone();
            bg.beams = s_button_beam.clone();
            bg.beams.setScale(2, 2);
            bg.beams.setAlpha(0.4);
            bg.effect = new SpineEffect(pos.x, pos.y, timer_animation, "spelleffect_timer", layer, 1.25);
            bg.effect.setSpeed(980 / PowerUpSettings.timeBetweenPlacementAndAction);
            bg.effect.setScale(bg.effect.animation.scale * PowerUpSettings.placementIconScale);
            if (!playController.isInitiatingPlayer) {
                bg.effect.flip();
            }
            bg.beams.setPosition(bg.beams.width / 2, bg.beams.height / 2);
            bg.timer = 0;
            bg.step = function (delta) {
                this.beams.setRotation(bg.beams.rotation + 0.001);
                this.timer += delta;
                if (this.timer >= PowerUpSettings.timeBetweenPlacementAndAction) {
                    self.removePlacedPowerupIcons();
                }
            };
            pwrupBgImg.setPosition(pwrupBgImg.width / 2 + 15, pwrupBgImg.height / 2 - 5);
            pwrupImg.setPosition(pwrupImg.width / 2 + element.iconOffset.x, pwrupImg.height / 2 + element.iconOffset.y + 7);
            pwrupBgImg.setScale(1.7, 1.7);
            bg.addChild(bg.beams);
            pwrupBgImg.addChild(pwrupImg);
            bg.addChild(pwrupBgImg);
            break;
        }
    }
    bg.power = power;
    bg.layerContainer = layer;
    getContainerForLayer(Layer.smokePuff).addChild(bg);
    bg.setPosition(pos.x, pos.y);
    bg.setScale(0.04 * PowerUpSettings.placementIconScale, 0.04 * PowerUpSettings.placementIconScale);
    this.placedPowerUpIcons.push(bg);
    return bg;
};
SceneController.prototype.swapPigs = function (pig1_id, pig2_id) {
    'use strict';
    var pig1 = this.levelBlocks.find(function (e) {
        return e.id === pig1_id;
    });
    var pig2 = this.levelBlocks.find(function (e) {
        return e.id === pig2_id;
    });
    if (pig1.constructor === Array) {
        if (pig1.length > 0)
            pig1 = pig1[0];
        else
            return;
    }
    if (pig2.constructor === Array) {
        if (pig2.length > 0)
            pig2 = pig2[0];
        else
            return;
    }
    var pigState1 = pig1.getPigState();
    pig1.swapEffect();
    pig2.swapEffect();
    pig1.setPigState(pig2.getPigState());
    pig2.setPigState(pigState1);
};
SceneController.prototype.createTNT = function (pos) {
    "use strict";
    var block = blockFactory("MISC_EXPLOSIVE_TNT", 10000000 + Math.floor(Math.random() * 1000000), pos.x, pos.y, 0, this.levelContainer);
    block.featherFall = true;
    block.invincible = true;
    block.onFirstContact = function () {
        this.featherFall = false;
    };
    this.checkPhysicsStop = 10;
    block.createParticleEffect(true);
    for (var i = 0; i < this.levelBlocks.length; i++) {
        this.levelBlocks[i].invincible = true;
    }
    this.levelBlocks.push(block);
};
SceneController.prototype.createIce = function (pos) {
    //new SpineEffect(pos.x, pos.y - PowerUpSettings.freezeRaySettings.yOffset, s_iceEffect, "s_snow_animation", this.levelContainer, PowerUpSettings.freezeRaySettings.effectScale );
    "use strict";
    var radius = PowerUpSettings.freezeRaySettings.radius;
    var objectList = this.findBlocksInRadius(pos, radius, ["Stone", "Wood"]);
    objectList.sort(function (a, b) {
        return a.dist - b.dist;
    });
    function iceObject(object) {
        object.changeMaterial("Ice");
        object.createParticleEffect(false);
    }
    for (var index = 0; index < objectList.length; index++) {
        iceObject(objectList[index].obj);
    }
};
SceneController.prototype.createStone = function (pos) {
    "use strict";
    //TODO: Make sure to add the animation here
    var radius = PowerUpSettings.stoneNatorSettings.radius;
    var objectList = this.findBlocksInRadius(pos, radius, ["Ice", "Wood"]);
    objectList.sort(function (a, b) {
        return a.dist - b.dist;
    });
    function stoneObject(object) {
        object.changeMaterial("Stone");
        object.createParticleEffect(false);
    }
    for (var index = 0; index < objectList.length; index++) {
        stoneObject(objectList[index].obj);
    }
};
SceneController.prototype.findBlocksInRadius = function (position, radius, materials) {
    var squaredRadius = radius * radius;
    var objectList = [];
    materials = [].concat(materials);
    for (var a = 0; a < this.levelBlocks.length; a++) {
        if (materials.indexOf(this.levelBlocks[a].definition.materialName) !== -1) {
            var dx = position.x - this.levelBlocks[a].x;
            var dy = position.y - this.levelBlocks[a].y;
            if (dx * dx + dy * dy < squaredRadius) {
                // shove in list!
                objectList.push({ obj: this.levelBlocks[a], dist: dx * dx + dy * dy });
            }
        }
    }
    return objectList;
};
SceneController.prototype.handlePhysicsStep = function () {
    if (ReplayLogger.isEnabled) {
        ReplayLogger.log(physics.getHash());
    }
    if (this.noCollisionSteps > 0) {
        this.noCollisionSteps--;
    }
    this.replayData.updateTime();
    if (this.playback) {
        this.playback.updateTime();
    }
    if (this.isPreparingLaunch) {
        var bird = blockFactory(this.selectedBirdName, -1, this.birdPosition.x, this.birdPosition.y, 0, this.levelContainer);
        this.balls.push(bird);
        this.getSlingshot().setBirdType(null);
        this.isPreparingLaunch = false;
    }
    if (this.fingerOne.touch) {
        this.fingerOne.x = tbGame.touchX(0);
        this.fingerOne.y = tbGame.touchY(0);
    }
    if (this.state == SceneControllerState.POWERUP_AVAILABLE) {
        this.managePowerUp();
    }
    if (this.stoppedIdling) {
        this.saveIdleEndState();
        this.stoppedIdling = false;
    }
    var i;
    for (i = 0; i < this.balls.length; i++) {
        var ball = this.balls[i];
        this.balls[i].physicsStep();
        if (this.balls[i].needToActivateSpecialPower) {
            this.activateBirdSpecialPower(this.balls[i]);
        }
        if (ball.x > this.killers.right || ball.x < this.killers.left || ball.y < this.killers.top || ball.y > this.killers.bottom) {
            ball.destroy();
            this.balls.splice(i, 1);
            i--;
            continue;
        }
        if (!ball.firstContact && ball.body) {
            ball.lookAt(ball.x + ball.velocity.x, ball.y + ball.velocity.y);
        }
    }
    for (i = 0; i < this.levelBlocks.length; i++) {
        var b = this.levelBlocks[i];
        this.levelBlocks[i].physicsStep();
        if (b.x > this.killers.right || b.x < this.killers.left || b.y < this.killers.top || b.y > this.killers.bottom) {
            this.destroyPhysicsObjectAndPlayFinalEffect(b);
            this.levelBlocks.splice(i, 1);
            i--;
            continue;
        }
        if (b.accumulatedScore > 0) {
            this.addScoreAndSpawnFloaters(b.isAlly ? 0 : b.accumulatedScore, b);
            b.accumulatedScore = 0;
        }
        if (b.isDead()) {
            this.levelBlocks.splice(i, 1);
            i--;
            if (b.isPig) {
                playController.updateUI();
            }
            this.checkPhysicsStop = this.idleBlockTimeout;
        }
    }
    this.destroyAllInactiveBirds();
    for (i = 0; i < this.explosions.length; i++)
        this.applyExplosion(this.explosions[i]);
    for (i = 0; i < this.coneBlasts.length; i++)
        this.applyConeBlast(this.coneBlasts[i]);
    this.explosions = [];
    this.coneBlasts = [];
    this.idleLastStep = this.allBlocksAreIdle;
    this.allBlocksAreIdle = true;
    for (i = 0; i < this.balls.length; i++) {
        if (!this.allBlocksAreIdle)
            break;
        if (this.balls[i].isActive())
            this.allBlocksAreIdle = false;
    }
    for (i = 0; i < this.levelBlocks.length; i++) {
        if (!this.allBlocksAreIdle)
            break;
        if (this.levelBlocks[i].isActive())
            this.allBlocksAreIdle = false;
    }
    if (this.allBlocksAreIdle) {
        if (this.checkPhysicsStop > 0) {
            this.checkPhysicsStop--;
            this.allBlocksAreIdle = false;
        }
        else {
            this.allBlocksAreIdle = true;
        }
    }
    else {
        this.checkPhysicsStop = this.idleBlockTimeout;
    }
    if (this.state == SceneControllerState.POWERUP_APPLYING && this.allBlocksAreIdle)
        this.changeState(SceneControllerState.BIRD_AVAILABLE);
    if (this.futureState != this.state) {
        this.changeStateDirect();
    }
    if (this.launchReady) {
        this.balls[this.balls.length - 1].launch(this.launchDirection, this.launchForce);
        this.replayData.setCameraPanStart();
        playController.sceneBuilder.trackBird();
        this.launchReady = false;
        this.removeAllInvincibility();
        this.removeAllFeatherfall();
        this.currentKillStreak = 0;
        if (this.firstBirdFired === false) {
            this.firstBirdFired = true;
        }
        this.noCollisionSteps = 1;
    }
    if (playController && this.allBlocksAreIdle && !playController.isShooting && !playController.uiController.uiPowerUps.getCurrentPowerUp()) {
        if (!this.idleLastStep || this.queuedIdleState || this.usedPowerup) {
            this.idleSteps = 0;
            this.replayData.setIdleStart();
            this.queuedIdleState = false;
            this.usedPowerup = false;
        }
        else {
            this.idleSteps++;
        }
    }
    else if (playController && this.allBlocksAreIdle && !this.idleLastStep && playController.isShooting) {
        this.queuedIdleState = true;
    }
    if (this.replayStateToLoad) {
        this.loadStateReplay(this.replayStateToLoad);
        this.replayStateToLoad = null;
    }
};
// handleDraw is called at the end of a step to draw the token.
SceneController.prototype.handleDraw = function () {
    "use strict";
    var i = 0;
    for (i = 0; i < this.balls.length; i++) {
        this.balls[i].draw();
    }
    for (i = 0; i < this.levelBlocks.length; i++) {
        this.levelBlocks[i].draw();
    }
};
SceneController.prototype.loadLevel = function (level) {
    "use strict";
    for (var a = 0; a < this.balls.length; a++) {
        this.balls[a].destroy();
    }
    for (var i = 0; i < this.levelBlocks.length; i++) {
        this.levelBlocks[i].destroy();
    }
    this.balls = [];
    this.levelBlocks = [];
    var arr = level["world"];
    var i;
    this.slingshotLeft.x = level["slingshotLeftX"];
    this.slingshotLeft.y = level["slingshotLeftY"] + PhysicsSettings.slingshotHeightOffset;
    this.slingshotRight.x = level["slingshotRightX"];
    this.slingshotRight.y = level["slingshotRightY"] + PhysicsSettings.slingshotHeightOffset;
    this.slingshotRight.container.setScale(-1, 1);
    //we use these offsets to move the level's origin at the midpoint between the 2 slingshots
    var offsetX = -(this.slingshotRight.x + this.slingshotLeft.x) / 2;
    var offsetY = 0;
    if (MiscSettings.levelExportMode === true) {
        this.offsetX = offsetX;
        this.offsetY = offsetY;
    }
    // this.levelOffsetContainer.setPosition(offsetX, offsetY);
    this.slingshotLeft.x += offsetX;
    this.slingshotLeft.y += offsetY;
    this.slingshotRight.x += offsetX;
    this.slingshotRight.y += offsetY;
    var bannerScale = 1 / PhysicsSettings.textureResolution * 1.2;
    var bannerScaleX = playController.isInitiatingPlayer ? bannerScale : -bannerScale;
    var leftSlingshot = this.getSlingshot(false);
    this.blueBanner = s_blueside.clone();
    this.blueBanner.setScale(bannerScaleX, bannerScale);
    this.blueBanner.setPosition(leftSlingshot.x, leftSlingshot.y);
    playController.sceneBuilder.bannerContainer.addChild(this.blueBanner);
    var rightSlingshot = this.getSlingshot(true);
    this.redBanner = s_redside.clone();
    this.redBanner.setScale(-bannerScaleX, bannerScale);
    this.redBanner.setPosition(rightSlingshot.x, rightSlingshot.y);
    playController.sceneBuilder.bannerContainer.addChild(this.redBanner);
    this.slingshotPerimeter.left = this.slingshotLeft.x + PowerUpSettings.slingshotOffset;
    this.slingshotPerimeter.right = this.slingshotRight.x - PowerUpSettings.slingshotOffset;
    this.killers.left = KillerPlanes.left + offsetX;
    this.killers.right = KillerPlanes.right + offsetX;
    this.killers.top = KillerPlanes.top + offsetY;
    this.killers.bottom = KillerPlanes.bottom + offsetY;
    this.killers.pigsBottom = KillerPlanes.pigsBottom + offsetY;
    var allMediumPigs = [];
    var allBigPigs = [];
    var allSmallPigs = [];
    var blockCount = level["counts"]["blocks"];
    var groupIDS = []; // cache ids
    var groupsArray = []; // actual lists of grouped pigs (use cache ids to retrieve)
    var groupTeams = []; // teams per id
    // PLACE IN GROUPS IF HAS ANGLE, ELSE PROCEED AS NORMAL
    for (i = 0; i < blockCount; i++) {
        var b = arr["block_" + (i + 1)];
        if (b["id"]["startsWith"]("PIG")) {
            if (MiscSettings.levelExportMode === true) {
                // we're in level export mode, we need to save the original pig angles
                if (!this._origPigAngles) {
                    this._origPigAngles = {};
                }
                this._origPigAngles["id_" + b["uniqueID"]] = b["angle"];
            }
            // round the angle and make sure it's clockwise
            var angle = b["angle"] > 0 ? Math.round(b["angle"]) : Math.floor(b["angle"]);
            if (angle < 0) {
                angle = angle + 360;
            }
            if (angle == 0) {
                // Not selected for group, just push to the standard randomize list
                if (b["id"] == "PIG_BASIC_MEDIUM") {
                    allMediumPigs.push(allMediumPigs.length % 2);
                }
                else if (b["id"] == "PIG_BASIC_BIG") {
                    allBigPigs.push(allBigPigs.length % 2);
                }
                else if (b["id"] == "PIG_BASIC_SMALL") {
                    allSmallPigs.push(allSmallPigs.length % 2);
                }
            }
            else {
                // Selected for group, handle group creation
                if (groupsArray[angle]) { // does array exist yet -> YES -> add to array
                    groupsArray[angle].push(b);
                }
                else { // does array exist yet -> NO -> create array, assign team and cache id
                    groupsArray[angle] = [b];
                    groupIDS.push(angle);
                    groupTeams.push(groupTeams.length % 2);
                }
            }
        }
    }
    if (groupIDS.length % 2 != 0) {
        console.log("GROUPS ARE NOT OF AN EQUAL NUMBER! PLEASE REVISIT THE LEVEL DESIGN.");
    }
    // Randomize group teams and make sure the teams are equal
    var equalTeams = false;
    var tries = 0;
    var maxTries = 100;
    while (equalTeams == false && tries < maxTries) {
        var blueMediumPigs = 0;
        var blueBigPigs = 0;
        var redMediumPigs = 0;
        var redBigPigs = 0;
        var shuffledGroup = this.shufflePigs(groupTeams);
        var copiedGroup = shuffledGroup.slice();
        for (var index = 0; index < groupIDS.length; index++) {
            var selectedGroup = groupIDS[index];
            var pigArray = groupsArray[selectedGroup];
            var isAlly = copiedGroup.pop();
            if (isAlly) {
                for (var i = 0; i < pigArray.length; i++) {
                    if (pigArray[i].id == "PIG_BASIC_MEDIUM") {
                        blueMediumPigs++;
                    }
                    else if (pigArray[i].id == "PIG_BASIC_BIG") {
                        blueBigPigs++;
                    }
                }
            }
            else {
                for (var i = 0; i < pigArray.length; i++) {
                    if (pigArray[i].id == "PIG_BASIC_MEDIUM") {
                        redMediumPigs++;
                    }
                    else if (pigArray[i].id == "PIG_BASIC_BIG") {
                        redBigPigs++;
                    }
                }
            }
        }
        if (blueMediumPigs == redMediumPigs && blueBigPigs == redBigPigs) {
            groupTeams = shuffledGroup;
            equalTeams = true;
        }
        tries++;
    }
    if (equalTeams == false)
        console.log("CANNOT BALANCE TEAMS! REVISIT LEVEL DESIGN!");
    for (var index = 0; index < groupIDS.length; index++) {
        var selectedGroup = groupIDS[index];
        var pigArray = groupsArray[selectedGroup];
        var isAlly = groupTeams.pop();
        for (var pigIndex = 0; pigIndex < pigArray.length; pigIndex++) {
            var pig = pigArray[pigIndex];
            var defName = pig["id"];
            defName += isAlly ? '_ALLY' : '_ENEMY';
            pig["id"] = defName;
            pig["angle"] = 0;
        }
    }
    allMediumPigs = this.shufflePigs(allMediumPigs);
    allBigPigs = this.shufflePigs(allBigPigs);
    allSmallPigs = this.shufflePigs(allSmallPigs);
    for (i = 0; i < blockCount; i++) {
        var b = arr["block_" + (i + 1)];
        var defName = b["id"];
        var isAlly = false;
        // TODO: TRY DEFNAME ENDS WITH ALLY
        if (defName["endsWith"]("ALLY")) {
            isAlly = true;
        }
        else if (defName == "PIG_BASIC_MEDIUM") {
            isAlly = allMediumPigs.pop();
            defName += isAlly ? '_ALLY' : '_ENEMY';
        }
        else if (defName == "PIG_BASIC_BIG") {
            isAlly = allBigPigs.pop();
            defName += isAlly ? '_ALLY' : '_ENEMY';
        }
        else if (defName == "PIG_BASIC_SMALL") {
            isAlly = allSmallPigs.pop();
            defName += isAlly ? '_ALLY' : '_ENEMY';
        }
        var isPig = defName["startsWith"]("PIG_");
        ;
        var container = isPig ? this.pigContainer : this.levelContainer;
        var block = blockFactory(defName, b["uniqueID"], b["x"] + offsetX, b["y"] + offsetY, b["angle"], container);
        block.isSlingshotIsland = block.x < this.slingshotPerimeter.left || block.x > this.slingshotPerimeter.right;
        if (block.isPig) {
            block.setSkin(Boolean(isAlly));
            this.pigBlocks.push(block);
        }
        this.levelBlocks.push(block);
    }
    var jointCount = level["counts"]["joints"];
    for (i = 0; i < jointCount; i++) {
        var jointData = arr["joint_" + (i + 1)];
        this.makeJoint(jointData["type"], this.levelBlocks[jointData["index1"]], this.levelBlocks[jointData["index2"]]);
    }
    if (level["exportedFromGame"]) {
        this._sleepAllObjects();
    }
    else {
        console.error("%cLEVEL NOT EXPORTED FROM GAME (FOR SETTLING); PLEASE REVIST", "color: red; font-size:15px;");
    }
};
SceneController.prototype.showTargetIndicators = function () {
    "use strict";
    var pigList = this.pigBlocks;
    if (!pigList)
        return;
    if (pigList.length == 0)
        return;
    for (var index = 0; index < pigList.length; index++) {
        var selectedPig = pigList[index];
        if (selectedPig.isAlly == false) {
            selectedPig.showTargetIndicator();
        }
        else {
            selectedPig.hideTargetIndicator();
        }
    }
};
SceneController.prototype.hideTargetIndicators = function () {
    "use strict";
    var pigList = this.pigBlocks;
    if (!pigList)
        return;
    if (pigList.length == 0)
        return;
    for (var index = 0; index < pigList.length; index++) {
        var selectedPig = pigList[index];
        if (selectedPig.isAlly == false) {
            selectedPig.hideTargetIndicator();
        }
    }
};
SceneController.prototype.shufflePigs = function (pigs) {
    "use strict";
    for (var i = 0; i < pigs.length; i++) {
        pigs.push(pigs.splice(Math.floor(this.customRandom(this.entryData["seed"]) * pigs.length), 1)[0]);
    }
    return pigs;
};
SceneController.prototype.makeJoint = function (kind, block, otherBlock) {
    "use strict";
    var joint_def;
    switch (kind) {
        case JointType.distance:
            console.error("Joint type DISTANCE not implemented!");
            break;
        case JointType.weld:
            joint_def = new Box2D.b2WeldJointDef();
            joint_def.Initialize(block.body, otherBlock.body, block.body.GetPosition());
            this.jointList.push({ joint: joint_def, one: block.id, two: otherBlock.id, kind: JointType.weld });
            break;
        case JointType.revolute:
            console.error("Joint type REVOLUTE not implemented!");
            break;
        case JointType.prismatic:
            console.error("Joint type PRISMATIC not implemented!");
            break;
        case JointType.destroy:
            console.error("Joint type DESTROY not implemented!");
            break;
    }
    physics.world.CreateJoint(joint_def);
};
SceneController.prototype.saveState = function () {
    "use strict";
    var savedState = {
        "timestamp": (new Date()).toString(),
        "blocks": "",
        "shields": [],
        "joints": [],
        "seed": -1,
        "initiatingPlayer": -1,
        "initiatingPlayerData": {},
        "otherPlayerData": {},
        "isGameOver": (this.state == SceneControllerState.GAME_OVER),
        "roundNumber": -1,
        "url": ""
    };
    if (this.entryData) {
        for (var a in this.entryData["initiatingPlayerData"]) {
            savedState["initiatingPlayerData"][a] = this.entryData["initiatingPlayerData"][a];
        }
        for (var a in this.entryData["otherPlayerData"]) {
            savedState["otherPlayerData"][a] = this.entryData["otherPlayerData"][a];
        }
        savedState["roundNumber"] = this.entryData["roundNumber"];
        savedState["initiatingPlayer"] = this.entryData["initiatingPlayer"];
        savedState["seed"] = this.entryData["seed"];
    }
    else {
        savedState["roundNumber"] = 0;
        savedState["initiatingPlayer"] = flowController.playerID;
        savedState["seed"] = Math.floor(Math.random() * 1e10);
        savedState["initiatingPlayerData"] = { "won": false, "id": tgfHooks.getPlayerID(), "name": tgfHooks.getPlayerName(), "img": tgfHooks.getPlayerImageUrl(), "totalPigs": this.countPigs(true), "score": 0 };
        savedState["otherPlayerData"] = { "won": false, "id": "", "name": "", "img": "", "totalPigs": this.countPigs(false), "score": 0 };
    }
    var tempArray = [];
    for (var i = 0; i < this.levelBlocks.length; i++) {
        tempArray = tempArray.concat(this.levelBlocks[i].snapAndGetSaveData());
        if (this.levelBlocks[i].isPig) {
            savedState["shields"].push(this.levelBlocks[i].getShield());
        }
    }
    savedState["blocks"] = compressData(tempArray);
    for (var a = 0; a < this.jointList.length; a++) {
        savedState["joints"].push(this.jointList[a].one, this.jointList[a].two, this.jointList[a].kind);
    }
    this.entryData = savedState;
};
SceneController.prototype.populateOpponent = function () {
    "use strict";
    if (this.entryData) {
        var name = playController.isInitiatingPlayer ? "initiatingPlayerData" : "otherPlayerData";
        if (!this.entryData[name]) {
            this.entryData[name] = {};
        }
        this.entryData[name]["name"] = tgfHooks.getPlayerName();
        this.entryData[name]["img"] = tgfHooks.getPlayerImageUrl();
        this.entryData[name]["id"] = tgfHooks.getPlayerID();
    }
};
SceneController.prototype.loadState = function (entryData) {
    "use strict";
    this.randomTracker = 0;
    if (entryData) {
        this.entryData = entryData;
    }
    else {
        console.error("SceneController.prototype.loadState - entryData not defined!");
    }
    this.loadLevel(this.levelData);
    if (this.entryData && entryData["blocks"]) {
        this.loadBlockState(entryData, entryData["blocks"]);
    }
    this.entryData["initiatingPlayerData"]["totalPigs"] = this.countPigs(true);
    this.entryData["otherPlayerData"]["totalPigs"] = this.countPigs(false);
};
SceneController.prototype.loadBlockState = function (entryData, arr) {
    'use strict';
    var shields = entryData["shields"];
    var i, j = 0;
    for (var i = 0; i < this.levelBlocks.length; i++) {
        this.levelBlocks[i].destroy();
    }
    this.resetPhysics();
    this.levelBlocks = [];
    this.jointList = [];
    var blockHashTable = {};
    for (i = 0; i < arr.length; i += 9) {
        var definition;
        for (var a in PhysicsObjectDefinition) {
            if (PhysicsObjectDefinition[a].generatedUID == arr[i + 5]) {
                definition = a;
                break;
            }
        }
        blockHashTable[arr[i]] = i / 9;
        var isPig = definition["startsWith"]("PIG_");
        var container = isPig ? this.pigContainer : this.levelContainer;
        var block = blockFactory(definition, arr[i], arr[i + 1], arr[i + 2], arr[i + 3] * (180 / Math.PI), container);
        block.health = block.maxHealth * arr[i + 4];
        block.setVelocity(arr[i + 6], arr[i + 7], arr[i + 8]);
        if (block.isPig) {
            if (shields) {
                if (shields[j] > 0) {
                    block.activateShield(shields[j]);
                }
                j++;
            }
            var isAllyPig = definition.indexOf('ALLY') !== -1;
            var isAlly = (playController.isInitiatingPlayer && isAllyPig) ||
                (!playController.isInitiatingPlayer && !isAllyPig);
            block.setSkin(isAlly);
            this.pigBlocks.push(block);
        }
        if (block.calcDamageThreshold() === 1) {
            block.hasDamage = true;
        }
        this.levelBlocks.push(block);
    }
    for (var a = 0; a < entryData["joints"].length; a += 3) {
        var block = this.levelBlocks[blockHashTable[entryData["joints"][a]]];
        var other = this.levelBlocks[blockHashTable[entryData["joints"][a + 1]]];
        if (block && other)
            this.makeJoint(entryData["joints"][a + 2], block, other);
    }
    if (playController.isShooting && this.state != SceneControllerState.REPLAYING) {
        this.showTargetIndicators();
    }
    this._sleepAllObjects();
};
SceneController.prototype._sleepAllObjects = function () {
    for (var i = 0; i < this.levelBlocks.length; i++) {
        var element = this.levelBlocks[i];
        element.setActive(false);
    }
};
SceneController.prototype.saveIdleEndState = function () {
    "use strict";
    if (this.idleSteps >= PhysicsSettings.maximumIdleReplaySteps) {
        this.saveBlocks();
        this.idleSteps = 0;
    }
    else {
        this.replayData.resetIdle();
    }
};
SceneController.prototype.saveBlocks = function () {
    "use strict";
    if (this.firstLevelSave && this.entryData && this.entryData["blocks"]) {
        var blocks = {};
        blocks["array"] = this.entryData["blocks"];
        blocks["joints"] = this.entryData["joints"];
        blocks["shields"] = this.entryData["shields"];
        this.replayData.setBlockState(blocks);
        this.replayStateToLoad = blocks;
        return;
    }
    var tempArray = [];
    var joints = [];
    var shields = [];
    var blocks = {};
    for (var i = 0; i < this.levelBlocks.length; i++) {
        tempArray = tempArray.concat(this.levelBlocks[i].snapAndGetSaveData());
        if (this.levelBlocks[i].isPig) {
            shields.push(this.levelBlocks[i].getShield());
        }
    }
    for (var a = 0; a < this.jointList.length; a++) {
        joints.push(this.jointList[a].one, this.jointList[a].two, this.jointList[a].kind);
    }
    blocks["array"] = tempArray;
    blocks["joints"] = joints;
    blocks["shields"] = shields;
    this.replayData.setBlockState(blocks);
    this.replayStateToLoad = blocks;
};
SceneController.prototype.changeState = function (newState) {
    "use strict";
    this.futureState = newState;
    console.log("Will Change State To: ", this.getStateName(newState));
};
SceneController.prototype.setSlingshotInput = function (enable) {
    "use strict";
    if (this.state != SceneControllerState.BIRD_AVAILABLE) {
        // not unlocking input because there is no bird
        return;
    }
    this.getSlingshot(false).setInputActive(enable);
};
SceneController.prototype.changeStateDirect = function () {
    "use strict";
    if (this.state == SceneControllerState.BIRD_AVAILABLE && this.futureState != SceneControllerState.BIRD_AVAILABLE) {
        this.getSlingshot().setInputActive(false);
        this.getSlingshot().setBirdType(null);
    }
    this.previousState = this.state;
    this.state = this.futureState;
    var ball;
    switch (this.state) {
        case SceneControllerState.NONE:
            break;
        case SceneControllerState.INITIALIZING:
            if (this.entryData["replay"] && playController._showReplay) {
                this.changeState(SceneControllerState.REPLAYING);
            }
            else {
                this.changeState(this.isCurrentPlayersTurn() ? SceneControllerState.TURN_STARTED : SceneControllerState.WAITING_FOR_OPPONENT);
            }
            if (this.entryData["roundNumber"] <= 3)
                playController.uiController.uiPowerUps.hide();
            break;
        case SceneControllerState.POWERUP_QUESTION:
            playController.messageHandler.hideAllMessages();
            if (this.previousState !== SceneControllerState.TURN_STARTED) {
                playController.uiController.uiPowerUps.hideGlows();
            }
            break;
        case SceneControllerState.POWERUP_AVAILABLE:
            playController.messageHandler.hideAllMessages(); // call when powerup
            playController.uiController.uiPowerUps.hideGlows();
            break;
        case SceneControllerState.POWERUP_APPLYING:
            break;
        case SceneControllerState.TURN_STARTED:
            this.firstLevelSave = true;
            // check for turn 1 / 2, execute start of game sequence first! then the rest
            // also the turns increment in 2s for some reason
            // i.e. turn 1 == 1, turn 2 == 3, turn 3 == 5
            var turn = this.entryData["roundNumber"];
            var self = this;
            if (playController.isAI && !playController.isFake) {
                var floater = new PracticeFloaterDialog({
                    onDone: function () {
                        playController.messageHandler.showText(tgfDictionary.getText("OBJECTIVE_BRIEF_TEXT"), 0, -250, 2500, 0.5, function callback() {
                            self.hideTargetIndicators();
                            playController.handlePowerupRefillDialog(function () {
                                self.afterPowerupRefillDialog();
                            });
                        });
                        floater.hide();
                        floater.free();
                    },
                });
                floater.show();
                floater.startAnimation();
            }
            else {
                playController.handleFirstTutorial(function () {
                    // SHOW TEXT 'DEFEAT THE RED PIGS'
                    if (turn <= 3) {
                        self.showTargetIndicators();
                        playController.messageHandler.showText(tgfDictionary.getText("OBJECTIVE_BRIEF_TEXT"), 0, -250, 2500, 0.5, function callback() {
                            self.hideTargetIndicators();
                            playController.handlePowerupRefillDialog(function () {
                                self.afterPowerupRefillDialog();
                            });
                        });
                    }
                    else {
                        playController.handlePowerupRefillDialog(function () {
                            self.afterPowerupRefillDialog();
                        });
                    }
                });
            }
            if (!playController.uiController.uiCards.getFirstCard()) {
                playController.uiController.uiCards.populateCards(this.entryData["roundNumber"]);
                playController.uiController.uiCards.selectedCard = 0;
                playController.uiController.uiCards.reAddCardUi();
            }
            break;
        case SceneControllerState.BIRD_AVAILABLE:
            playController.checkMightyScope();
            if (!playController.uiController.uiCards.getFirstCard()) {
                playController.messageHandler.hideAllMessages(); // call when powerup
                playController.uiController.uiCards.populateCards(this.entryData["roundNumber"]);
                playController.uiController.uiCards.selectedCard = 0;
                playController.uiController.uiCards.reAddCardUi();
            }
            this.selectedBirdName = playController.uiController.uiCards.getCurrentBirdType();
            this.sounds = PhysicsObjectDefinition[this.selectedBirdName].sounds;
            this.getSlingshot().setInputActive(true);
            this.spawnBirdOnSlingshot();
            playController.uiController.cardGlow.hide();
            break;
        case SceneControllerState.GAME_WORKING:
            this.firstLevelSave = false;
            this.uiWaitTime = 0;
            playController.sceneBuilder.cameraAnimation = true;
            playController.sceneBuilder.skipPanningRequest = true;
            this.allBlocksAreIdle = false;
            this.checkPhysicsStop = this.idleBlockTimeout;
            ball = this.balls[this.balls.length - 1];
            this.launchReady = true;
            if (!this.singleplayer)
                playController.uiController.uiCards.discardSelection();
            this._destroyAllBirdTrails();
            this.birdTrail = new BirdTrail(this.birdTrailContainer, this.balls);
            if (this.onLaunchSelected)
                this.onLaunchSelected();
            break;
        case SceneControllerState.WAITING_FOR_OPPONENT:
            playController.isIngame = true;
            if (this.isCurrentPlayersTurn()) {
                if (playController.infiniteBirds) {
                    this.changeState(SceneControllerState.BIRD_AVAILABLE);
                    break;
                }
                playController.uiController.uiPowerUps.setState(playController.uiController.uiPowerUps.STATE.NO_ANIMATION);
                DailyRewardManager.onFinishTurn();
                inviteFriendManager.onFinishTurn();
                this.saveState();
                this.replayData.setEnd();
                this.entryData["replay"] = this.replayData.getCompressedData();
                this.entryData["replayCamera"] = this.replayData.getCameraData();
                this._sleepAllObjects();
                playController.saveAndExit(this.entryData);
                ReplayLogger.stopRecording();
            }
            break;
        case SceneControllerState.REPLAYING:
            playController.sceneBuilder.cameraAnimation = true;
            playController.sceneBuilder.skipPanningRequest = true;
            this.allBlocksAreIdle = false;
            break;
        case SceneControllerState.GAME_OVER:
            this.saveState();
            this.replayData.setEnd();
            this.entryData["replay"] = this.replayData.getCompressedData();
            this.entryData["replayCamera"] = this.replayData.getCameraData();
            this.entryData["watchedFinalReplay"] = 0;
            playController.saveAndExit(this.entryData);
            ReplayLogger.stopRecording();
            break;
    }
};
SceneController.prototype.afterPowerupRefillDialog = function () {
    playController.showPowerupBar();
    this.changeState(SceneControllerState.BIRD_AVAILABLE);
    playController.sceneBuilder.cameraToSlingshot(true);
};
SceneController.prototype.removeAllInvincibility = function () {
    "use strict";
    for (var b = 0; b < this.levelBlocks.length; b++) {
        this.levelBlocks[b].invincible = false;
    }
};
SceneController.prototype.removeAllFeatherfall = function () {
    "use strict";
    for (var b = 0; b < this.levelBlocks.length; b++) {
        this.levelBlocks[b].featherFall = false;
    }
};
SceneController.prototype.selectBird = function (birdType) {
    "use strict";
    if (this.state == SceneControllerState.BIRD_AVAILABLE) {
        AngryAnalytics.reportCardChange(birdType);
        // this.destroyAllBirds();
        this.selectedBirdName = birdType;
        this.idleTimer = PhysicsObjectDefinition[birdType].soundOptions.idleTimer;
        this.sounds = PhysicsObjectDefinition[birdType].sounds;
        this._timer = this.idleTimer;
        this.isReleased = false;
        this.birdSelected = true;
        this.spawnBirdOnSlingshot();
        playController.sceneBuilder.cameraToSlingshot(false);
    }
    else if (this.state == SceneControllerState.POWERUP_QUESTION
        || this.state == SceneControllerState.TURN_STARTED
        || this.state == SceneControllerState.POWERUP_AVAILABLE) {
        this.changeState(SceneControllerState.BIRD_AVAILABLE);
        playController.messageHandler.hideAllMessages();
    }
};
SceneController.prototype.activateBirdSpecialPower = function (bird) {
    "use strict";
    if (bird.activateSpecialPower(this.balls)) {
        this.birdTrail.usedSpecialAbility();
        this.replayData.setSpecial();
    }
    bird.needToActivateSpecialPower = false;
};
SceneController.prototype._destroyAllBirdTrails = function () {
    if (this.birdTrail) {
        tbGame.removeToken(this.birdTrail);
    }
};
SceneController.prototype.spawnBirdOnSlingshot = function () {
    if (!this.selectedBirdName) {
        this.selectedBirdName = "RedBird";
    }
    this.getSlingshot().setBirdType(this.selectedBirdName);
};
SceneController.prototype.slingshotDragCallback = function () {
    if (this.countPigs(true) == 0 || this.countPigs(false) == 0) {
        // All of the pigs are already dead
        return;
    }
    playController.isShooting = true;
    this.stoppedIdling = true;
    SoundManager.playEffect(SoundManager.BIRD_STATE.STRETCH, this.sounds);
    SoundManager.playEffect(SoundManager.SLINGSHOT_STATE.STRETCH, SoundManager.MaterialCollection.SLINGSHOT, 0, true);
    playController.sceneBuilder.trackBird();
    playController.messageHandler.hideAllMessages();
    if (this.state != SceneControllerState.REPLAYING) {
        this.showTargetIndicators();
    }
};
SceneController.prototype.slingshotEndDragCallback = function (isCancelled) {
    playController.isShooting = false;
    if (this.countPigs(true) == 0 || this.countPigs(false) == 0) {
        // All of the pigs are already dead
        isCancelled = true;
    }
    if (!isCancelled && this.selectedBirdName) {
        AngryAnalytics.reportShot();
        this.birdPosition = this.getSlingshot().birdPosition;
        this.isReleased = true;
        this.launchForce = this.getSlingshot().launchForce;
        this.launchDirection = this.getSlingshot().launchDirection.clone();
        var shot = {
            "birdPosition": this.birdPosition,
            "selectedBirdName": this.selectedBirdName,
            "launchForce": this.launchForce,
            "launchDirection": this.launchDirection
        };
        this.replayData.setShot(shot);
        this.changeState(SceneControllerState.GAME_WORKING);
        this.isPreparingLaunch = true;
        this.getSlingshot().setInputActive(false);
        tgfAudio.stop("game");
        SoundManager.playEffect(SoundManager.SLINGSHOT_STATE.FIRE, SoundManager.MaterialCollection.SLINGSHOT);
    }
    else {
        tgfAudio.stop("game");
        this.replayData.deleteLastState("blocks");
        this.spawnBirdOnSlingshot();
    }
    this.hideTargetIndicators();
};
SceneController.prototype.removeBlocksNearSlingshot = function () {
    "use strict";
    var slingshot = this.getSlingshot();
    for (var i = this.levelBlocks.length - 1; i > 0; i--) {
        var e = this.levelBlocks[i];
        var distance = Math.sqrt((slingshot.x - e.x) * (slingshot.x - e.x) + (slingshot.y - e.y) * (slingshot.y - e.y));
        if (distance < MiscSettings.blocksNearSlingshotDistance) {
            console.log("Destroying Blocks Near Slingshot: " + e.definitionName);
            // This awards points, but that's fine because this is only for edge cases where a player miraculously
            // manages to get an object on the platform near the slingshot. Frankly they deserve the points.
            //e.applyDamage( e.health + e.material.defence * 2 );
            e.destroy();
            this.levelBlocks.splice(i, 1);
        }
    }
};
SceneController.prototype.slingshotReplayShot = function (data) {
    this.removeBlocksNearSlingshot();
    var birdPosition = data["birdPosition"];
    var bird = blockFactory(data.selectedBirdName, -1, birdPosition.x, birdPosition.y, 0, this.levelContainer);
    this.balls.push(bird);
    this._destroyAllBirdTrails();
    this.birdTrail = new BirdTrail(this.birdTrailContainer, this.balls);
    this.isReleased = true;
    this.launchForce = data["launchForce"];
    this.launchDirection = data["launchDirection"];
    this.getSlingshot(true).setBirdType(null);
    tgfAudio.stop("game");
    SoundManager.playEffect(SoundManager.SLINGSHOT_STATE.FIRE, SoundManager.MaterialCollection.SLINGSHOT);
    this.launchReady = true;
};
SceneController.prototype.loadStateReplay = function (entryData) {
    this.randomTracker = 0;
    if (!entryData) {
        console.log("Something went wrong");
        return;
    }
    this.loadBlockState(entryData, entryData["array"]);
};
SceneController.prototype.specialReplayShot = function () {
    "use strict";
    if (this.balls.length > 0) {
        this.balls[this.balls.length - 1].needToActivateSpecialPower = true;
    }
};
SceneController.prototype.afterReplay = function (data) {
    this._sleepAllObjects();
    this._destroyAllBirdTrails();
    this.replayData.reset();
    this.changeState(SceneControllerState.TURN_STARTED);
    this.allBlocksAreIdle = false;
    this.entryData = data;
};
SceneController.prototype.setupSlingshots = function () {
    var self = this;
    var callbackStartDrag = function () { self.slingshotDragCallback(); };
    var callbackEndDrag = function (isCancelled) { self.slingshotEndDragCallback(isCancelled); };
    this.slingshotLeft = new Slingshot(0, 0, this.depth, playController.isInitiatingPlayer);
    this.slingshotLeft.callbackStartDrag = callbackStartDrag;
    this.slingshotLeft.callbackEndDrag = callbackEndDrag;
    this.slingshotRight = new Slingshot(0, 0, this.depth, !playController.isInitiatingPlayer);
    this.slingshotRight.callbackStartDrag = callbackStartDrag;
    this.slingshotRight.callbackEndDrag = callbackEndDrag;
};
SceneController.prototype.handleEntryData = function (en) {
    this.entryData = en;
    this.populateOpponent();
    this.loadState(this.entryData);
    this.changeState(SceneControllerState.INITIALIZING);
};
SceneController.prototype.isCurrentPlayersTurn = function () {
    return (this.entryData["lastTurnID"] != flowController.playerID);
};
SceneController.prototype.getSlingshot = function (isOtherPlayer) {
    if (isOtherPlayer) { // only passed when you need your opponent's slingshot (for positions i.e.)
        return (playController.isInitiatingPlayer ? this.slingshotRight : this.slingshotLeft);
    }
    else { // default for your own slingshot
        return (playController.isInitiatingPlayer ? this.slingshotLeft : this.slingshotRight);
    }
};
SceneController.prototype.addConeBlast = function (x, y, angle) {
    this.coneBlasts.push({
        x: x,
        y: y,
        angle: angle
    });
};
SceneController.prototype.applyConeBlast = function (blast) {
    var center = new tbVector(blast.x, blast.y);
    var direction = new tbVector(Math.cos(blast.angle), Math.sin(blast.angle));
    for (var a = 0; a < this.levelBlocks.length; a++) {
        var block = this.levelBlocks[a];
        if (!block.body)
            continue;
        var blockCenter = new tbVector(block.x, block.y);
        var delta = center.subtract(blockCenter);
        if (delta.length() < AbilitySettings.red.range) {
            var angleDifference = blast.angle - Math.atan2(-delta.y, -delta.x);
            if (angleDifference < -Math.PI)
                angleDifference += 2 * Math.PI;
            if (angleDifference > Math.PI)
                angleDifference -= 2 * Math.PI;
            if (Math.abs(angleDifference) > AbilitySettings.red.spread)
                continue;
            physics.applyLinearImpulse(block.body, direction, AbilitySettings.red.force);
        }
    }
};
SceneController.prototype.addExplosion = function (x, y, explosionType) {
    var explosion = new Explosion(x, y, explosionType.pushRadius, explosionType.push, explosionType.damageRadius, explosionType.damage);
    this.explosions.push(explosion);
    this.cameraShake = CameraSettings["cameraShake"]["explosionShake"];
};
SceneController.prototype.applyExplosion = function (explosion) {
    new ExplosionToken(explosion.x, explosion.y);
    var origin = new tbVector(explosion.x, explosion.y);
    for (var i = 0; i < this.levelBlocks.length; i++) {
        var block = this.levelBlocks[i];
        var body = block.body;
        if (!body) {
            continue;
        }
        var bodyCenter = new tbVector(block.x, block.y);
        var dist = (origin.subtract(bodyCenter)).length();
        if (dist < explosion.pushRadius) {
            var push = explosion.push / dist;
            var pushDirection = (bodyCenter.subtract(origin)).normalize();
            if (body.physicsObj instanceof BalloonObjectDefault) {
                block.applyDamage(Math.ceil(block.maxHealth));
            }
            physics.applyLinearImpulse(body, pushDirection, push);
            //console.log("Applying explosion PUSH (id: " + block.id + ", push: " + push + ", dist: " + dist + ")");
        }
        if (dist < explosion.damageRadius) {
            var damage = explosion.damage / dist;
            block.applyDamage(damage);
            //console.log("Applying explosion DAMAGE (id: " + block.id + ", damage: " + damage + ")");
        }
    }
};
SceneController.prototype.destroyAllBirds = function () {
    for (var i = 0; i < this.balls.length; i++) {
        this.balls[i].destroy();
    }
    this.balls = [];
};
SceneController.prototype.destroyAllInactiveBirds = function () {
    for (var i = 0; i < this.balls.length; i++) {
        if (this.balls[i] && !this.balls[i].isActive() && this.balls[i].firstContact) {
            ReplayLogger.log("destroyed a bird");
            this.balls[i].destroy();
            this.balls.splice(i, 1);
            i--;
        }
    }
};
SceneController.prototype.customRandom = function (seed) {
    this.randomTracker = (432121413 * this.randomTracker + 43219612 + seed) % 4321241 * (7914369 + seed);
    return 1e-6 * (this.randomTracker % 1e6);
};
SceneController.prototype.countPigs = function (countInitiatingPlayersPigs) {
    var pigsLeft = 0;
    for (var i = 0; i < this.levelBlocks.length; i++) {
        var b = this.levelBlocks[i];
        if (b.isPig) {
            var myPig = (b.isFirstPig && countInitiatingPlayersPigs) || (!b.isFirstPig && !countInitiatingPlayersPigs);
            if (myPig)
                pigsLeft++;
        }
    }
    return pigsLeft;
};
SceneController.prototype.PreSolveHandler = function (pContact) {
    'use strict';
    /** @type Module.b2Contact */
    var contact = Box2D.wrapPointer(pContact, Box2D.b2Contact);
    /** @type Module.b2Fixture */
    var fixtureA = contact.GetFixtureA();
    /** @type Module.b2Fixture */
    var fixtureB = contact.GetFixtureB();
    /** @type Module.b2Body */
    var bodyA = fixtureA.GetBody();
    /** @type Module.b2Body */
    var bodyB = fixtureB.GetBody();
    var bothArePhysicsObjects = bodyA.physicsObj && bodyB.physicsObj;
    if (bothArePhysicsObjects) {
        if (bodyA.physicsObj.birdObject && bodyB.physicsObj.definition.materialName === 'Balloon' ||
            bodyB.physicsObj.birdObject && bodyA.physicsObj.definition.materialName === 'Balloon') {
            //when one of the physicsObjs is a bird and the other one is a balloon, immediately skip to damage handler and skip the pysics calculations.
            this.contactHandler(pContact);
            contact["SetEnabled"](false);
        }
        if (bodyA.physicsObj.definition.materialName === 'Balloon') {
            bodyA.physicsObj.collTimer = 1;
        }
        else if (bodyB.physicsObj.definition.materialName === 'Balloon') {
            bodyB.physicsObj.collTimer = 1;
        }
        if (bodyA.physicsObj.birdObject) {
            if (this.noCollisionSteps > 0) {
                contact["SetEnabled"](false);
            }
        }
        else if (bodyB.physicsObj.birdObject) {
            if (this.noCollisionSteps > 0) {
                contact["SetEnabled"](false);
            }
        }
    }
};
SceneController.prototype.contactHandler = function (pContact) {
    /** @type Module.b2Contact */
    var contact = Box2D.wrapPointer(pContact, Box2D.b2Contact);
    /** @type Module.b2Fixture */
    var fixtureA = contact.GetFixtureA();
    /** @type Module.b2Fixture */
    var fixtureB = contact.GetFixtureB();
    /** @type Module.b2Body */
    var bodyA = fixtureA.GetBody();
    /** @type Module.b2Body */
    var bodyB = fixtureB.GetBody();
    var v = bodyA.GetLinearVelocity();
    var velocityA = new tbVector(v.get_x(), v.get_y());
    v = bodyB.GetLinearVelocity();
    var velocityB = new tbVector(v.get_x(), v.get_y());
    var massA = bodyA.GetMass();
    var massB = bodyB.GetMass();
    var forceX = (massA * velocityA.x) - (massB * velocityB.x);
    var forceY = (massA * velocityA.y) - (massB * velocityB.y);
    var force = (Math.sqrt(forceX * forceX + forceY * forceY) / 10) / Math.pow(physics.scale, 3);
    var damageMuliplierB = getDamageMultiplier(bodyB.physicsObj, bodyA.physicsObj);
    var damageMuliplierA = getDamageMultiplier(bodyA.physicsObj, bodyB.physicsObj);
    var oldHealthA = bodyA.physicsObj ? bodyA.physicsObj.health : -1;
    var oldHealthB = bodyB.physicsObj ? bodyB.physicsObj.health : -1;
    if (this.noCollisionSteps > 0) {
        return;
    }
    //don't apply damage if the bodies are both birds (can happen with the blue bird)
    var bothAreBirds = bodyA.physicsObj && bodyB.physicsObj && bodyA.physicsObj.birdObject && bodyB.physicsObj.birdObject;
    if (bodyA.physicsObj && !bothAreBirds) {
        bodyA.physicsObj.applyDamage(force * damageMuliplierB);
    }
    if (bodyB.physicsObj && !bothAreBirds) {
        bodyB.physicsObj.applyDamage(force * damageMuliplierA);
    }
    if (bodyA.physicsObj && bodyB.physicsObj) {
        if (bodyA.physicsObj.birdObject && bodyB.physicsObj.isDead()) {
            bodyA.physicsObj.birdObject.applyBirdSpeedBoost(force * damageMuliplierA, oldHealthB, getVelocityMultiplier(bodyA.physicsObj, bodyB.physicsObj));
        }
        if (bodyB.physicsObj.birdObject && bodyA.physicsObj.isDead()) {
            bodyB.physicsObj.birdObject.applyBirdSpeedBoost(force * damageMuliplierB, oldHealthA, getVelocityMultiplier(bodyB.physicsObj, bodyA.physicsObj));
        }
    }
};
SceneController.prototype.PostSolveHandler = function (pContact) {
    'use strict';
    /** @type Module.b2Contact */
    var contact = Box2D.wrapPointer(pContact, Box2D.b2Contact);
    /** @type Module.b2Fixture */
    var fixtureA = contact.GetFixtureA();
    /** @type Module.b2Fixture */
    var fixtureB = contact.GetFixtureB();
    /** @type Module.b2Body */
    var bodyA = fixtureA.GetBody();
    /** @type Module.b2Body */
    var bodyB = fixtureB.GetBody();
    var bothArePhysicsObjects = bodyA.physicsObj && bodyB.physicsObj;
    if (bothArePhysicsObjects) {
        var psoA = bodyA.physicsObj, psoB = bodyB.physicsObj;
        if (psoA.isSlingshotIsland && psoB.body !== null) {
            this.destroyPhysicsObjectAndPlayFinalEffect(psoB);
        }
        if (psoB.isSlingshotIsland && psoA.body !== null) {
            this.destroyPhysicsObjectAndPlayFinalEffect(psoA);
        }
    }
};
SceneController.prototype.destroyPhysicsObjectAndPlayFinalEffect = function (phObj) {
    'use strict';
    phObj.destroy();
    phObj.finalEffect();
};
SceneController.getRandomCards = function (roundForPull, birdOptions) {
    if (!birdOptions) {
        birdOptions = CardSettings.birdOptions;
    }
    var cards = [];
    var distributionToPullFrom;
    for (var a = 0; a < birdOptions.length; a++) {
        if (roundForPull >= birdOptions[a].fromTurn && roundForPull <= birdOptions[a].lastTurn) {
            distributionToPullFrom = birdOptions[a].options;
            break;
        }
    }
    if (!distributionToPullFrom)
        console.log("There are no bird options specified for this turn! " + roundForPull + " had no options lined up. check the card settings!");
    var birdsPickedMap = {};
    for (var c = 0; c < CardSettings.cardsPerTurn; c++) {
        var sum = 0;
        for (var a = 0; a < distributionToPullFrom.length; a++) {
            if (birdsPickedMap[distributionToPullFrom[a].bird] && birdsPickedMap[distributionToPullFrom[a].bird] >= distributionToPullFrom[a].max)
                continue;
            sum += distributionToPullFrom[a].chance;
        }
        var takeOut = [];
        var edgeTracker = 0;
        for (var a = 0; a < distributionToPullFrom.length; a++) {
            var optionsLeft = distributionToPullFrom[a].max;
            if (birdsPickedMap[distributionToPullFrom[a].bird])
                optionsLeft -= birdsPickedMap[distributionToPullFrom[a].bird];
            if (optionsLeft < 1)
                continue;
            for (var b = 0; b < optionsLeft; b++) {
                takeOut.push({ edge: edgeTracker, i: a });
                edgeTracker += distributionToPullFrom[a].chance / (sum * optionsLeft);
            }
        }
        var choiceValue = Math.random();
        var choice;
        for (var a = takeOut.length - 1; a > -1; a--) {
            if (choiceValue > takeOut[a].edge) {
                choice = distributionToPullFrom[takeOut[a].i].bird;
                break;
            }
        }
        if (birdsPickedMap[choice])
            birdsPickedMap[choice]++;
        else
            birdsPickedMap[choice] = 1;
        cards.push(choice);
    }
    // shuffle the array
    for (var a = 0; a < cards.length; a++) {
        cards.push(cards.splice(Math.floor(Math.random() * cards.length), 1)[0]);
    }
    return cards;
};
// Returns true if there are two valid swappable pigs
// otherwise returns false
SceneController.prototype.canSwapPigs = function () {
    for (var i = 0; i < this.levelBlocks.length; i++) {
        var a = this.levelBlocks[i];
        if (a.isPig) {
            var pigAsize = a.getSize();
            for (var n = i + 1; n < this.levelBlocks.length; n++) {
                var b = this.levelBlocks[n];
                if (b.isPig) {
                    var pigBsize = b.getSize();
                    if (pigAsize == pigBsize) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
};
SceneController.prototype.onPowerupCanceled = function () {
    this.replayData.deleteLastState("blocks");
    this.cleanUpPowerUp(false);
    this.removeSelectedPigEffect();
};
SceneController.prototype.isPlacingPowerup = function () {
    return this.placedPowerUpIcons.length > 0;
};
